"use client";

import { useEffect, useMemo, useRef, useState } from "react";

type UseHashActionOptions = {
  /**
   * Whether this hook should be active
   * @default true
   */
  enabled?: boolean;
  /**
   * Maximum number of retries for async content
   * @default 50
   */
  retries?: number;
  /**
   * Interval between retries in milliseconds
   * @default 100
   */
  retryInterval?: number;
};

/**
 * Hook for components to perform actions when specific hash values are present in the URL
 *
 * @param hashPattern - One or more hash values to match against (without the #)
 * @param onMatch - Callback function that executes when hash matches. Receives the matched hash value
 * @param options - Optional configuration options
 * @returns The currently matched hash value (or null if no match)
 *
 * @example
 * ```tsx
 * // Single hash pattern
 * function MyComponent() {
 *   const matchedHash = useHashAction("my-section", (hash) => {
 *     console.log("Hash matched:", hash);
 *     // Perform action here
 *   });
 *   return <div>Matched: {matchedHash}</div>;
 * }
 *
 * // Multiple hash patterns
 * function TabsComponent() {
 *   const matchedHash = useHashAction(
 *     ["tab-1", "tab-2", "tab-3"],
 *     (hash) => {
 *       // Set active tab based on hash
 *       setActiveTab(hash);
 *     }
 *   );
 *   return <div>Active: {matchedHash}</div>;
 * }
 *
 * // With options
 * useHashAction("my-hash", handleMatch, { enabled: true, retries: 30 });
 * ```
 */
export function useHashAction(
  hashPattern: string | string[],
  onMatch: (hash: string) => void,
  options?: UseHashActionOptions,
): string | null {
  const { enabled = true, retries = 50, retryInterval = 100 } = options || {};
  const [matchedHash, setMatchedHash] = useState<string | null>(null);

  const hashPatterns = useMemo(
    () => (Array.isArray(hashPattern) ? hashPattern : [hashPattern]),
    [hashPattern],
  );
  const hashPatternsRef = useRef(hashPatterns);
  const onMatchRef = useRef(onMatch);

  // Update refs when values change
  useEffect(() => {
    hashPatternsRef.current = hashPatterns;
  }, [hashPatterns]);

  useEffect(() => {
    onMatchRef.current = onMatch;
  }, [onMatch]);

  useEffect(() => {
    if (!enabled || hashPatterns.length === 0) {
      setMatchedHash(null);
      return;
    }

    const checkAndExecute = (): boolean => {
      const hash = window.location.hash.slice(1);
      if (!hash) {
        setMatchedHash(null);
        return false;
      }

      // Check if hash matches any of the patterns
      const matchedPattern = hashPatternsRef.current.find((pattern) => pattern === hash);
      if (matchedPattern) {
        setMatchedHash(matchedPattern);
        onMatchRef.current(matchedPattern);
        return true;
      }

      setMatchedHash(null);
      return false;
    };

    // Check on mount
    const initialMatch = checkAndExecute();

    // Listen for hash changes
    const handleHashChange = () => {
      checkAndExecute();
    };

    window.addEventListener("hashchange", handleHashChange);

    // Also check periodically in case content loads asynchronously
    // This helps with components that render after initial page load
    let retryCount = 0;
    let intervalId: NodeJS.Timeout | null = null;

    if (!initialMatch) {
      intervalId = setInterval(() => {
        if (checkAndExecute() || retryCount >= retries) {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }
        retryCount++;
      }, retryInterval);
    }

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [enabled, hashPatterns, retries, retryInterval]);

  return matchedHash;
}
